import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';

import { getContractStatusTone } from '../types/contractStatus';
import type { ParcelSummary } from '../types/parcels';

/**
 * Deriva contagens e totais a partir da lista de parcelas.
 *
 * O endpoint de parcelas não devolve nenhum agregado, então as linhas de apoio
 * da régua de indicadores e o rodapé da tabela saem daqui.
 *
 * A classificação de paga/em aberto reusa `getContractStatusTone`, o mesmo
 * casamento por substring normalizada usado nas `Tag` de status — assim a
 * contagem nunca discorda do que a tabela mostra, mesmo quando o backend
 * introduz um texto de status novo.
 *
 * @param parcels - Parcelas do contrato.
 * @returns Contagens (total, pagas, em aberto) e somas de valor previsto e pago.
 *
 * @example
 * ```ts
 * const { paid, open, expectedValue } = deriveParcelSummary(parcels);
 * ```
 */
export function deriveParcelSummary(
  parcels: readonly IndividualPaymentParcelViewModel[],
): ParcelSummary {
  let paid = 0;
  let open = 0;
  let expectedValue = 0;
  let paidValue = 0;

  for (const parcel of parcels) {
    const tone = getContractStatusTone(parcel.statusText);
    if (tone === 'positive') paid += 1;
    else if (tone === 'helper') open += 1;

    expectedValue += parcel.total ?? 0;
    paidValue += parcel.totalPaid ?? 0;
  }

  return { total: parcels.length, paid, open, expectedValue, paidValue };
}
